import React, { useEffect, useState } from "react";
import {
    Badge, Button, Checkbox, CircularProgress, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useTheme
} from "@mui/material";
import { connect } from "react-redux";
//--------------------------|| 3RD PARTY LIBRARIES ||------------------------------------//
import {
    IconCheck,
    IconEdit,
    IconFilter,
    IconPlaylistAdd,
    IconSortAscending2,
    IconSortDescending2,
    IconX
} from "@tabler/icons-react";
//--------------------------|| FILES IMPORT ||------------------------------------------//
import { getPager } from "helper/common";
import MainCard from "ui-component/cards/MainCard";
import SubCard from "ui-component/cards/SubCard";
import { NoData } from "ui-component/custom-components/nodata";
import { VWPagination } from "ui-component/custom-components/pagination";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import Chip from "ui-component/extended/Chip";
import moment from "moment";
import {
    clearDistrictList,
    closeDistrictCreateDialog,
    closeDistrictFilterDialog,
    districtEdit,
    districtEditErrorClear,
    districtListErrorClear,
    getDistrictList,
    getStateOptionCreate,
    openDistrictCreateDialog,
    openDistrictFilterDialog,
    setDistrictFilteredData
} from "store/app/form-management/district-configuration/actions";
import CreateDistrict from "./create";
import FilterDistrict from "./filter";
import { AllowedSpecialCharacters, AllowedSpecialCharactersPaste } from "helper/regex";
import VWDropdown from "components/dropdown";
import { unionBy } from 'lodash';

const tableHeaderStyle = { paddingLeft: '0px' }

const DistrictList = (props) => {
    //--------------------List--------------------------------------
    const { districtList, totalCounts, isPending, errorMessage } = props;

    const [pageSize, setPageSize] = useState(10);
    const [Pager, setPager] = useState({ currentPage: 1 });
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortOrder, setSortOrder] = useState(null);

    //-------------------------State Details Tab--------------------
    const { selectedState, selectedStateId, stateStatus } = props;
    //--------------------Filter------------------------------------
    const { districtFilterData, isDistrictFilterDialogOpen } = props;
    //--------------------Create------------------------------------
    const { isDistrictCreateDialogOpen } = props;
    //--------------------Edit--------------------------------------
    const { districtEditErrorMessage, isDistrictEditPending } = props;

    const [isEdit, setIsEdit] = useState();
    const [updateDistrict, setUpdateDistrict] = useState();
    const [errors, setErrors] = useState();

    //--------------------Profile-----------------------------------
    const { userProfile } = props;
    const { userRoleName } = userProfile || {};

    //--------------------UI Design---------------------------------
    const theme = useTheme();
    const colorPrimary = theme.palette.secondary.dark;
    const [tableHeight, setTableHeight] = useState(window.innerHeight);

    //--------------------State---------------------------
    const [stateOptions, setStateOptions] = useState([]);

    const loadStateOption = (filter, page, pageSize) => {
        return props.getStateOption(filter, page, pageSize)
    }

    //--------------------------------------------------------------
    useEffect(() => {
        props.districtListErrorClear();
        props.districtEditErrorClear();

        if (selectedState) {
            props.setDistrictFilteredData({ ...districtFilterData, statename: selectedState });
        }

        const handleResize = () => {
            setTableHeight(window.innerHeight)
        };
        window.addEventListener("resize", handleResize);
        return () => {
            props.clearDistrictList();
            window.removeEventListener("resize", handleResize);
        }
    }, [])

    //--------------------------|| SORTING ||------------------------------------------//
    const handleSort = (column) => {
        setIsEdit();
        if (sortOrder === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortOrder(column);
            setSortDirection('asc');
        }
    };

    //--------------------------|| PAGINATION ||------------------------------------------//
    const setPage = (page = 1) => {
        setIsEdit();
        props.getDistrictList(page, pageSize, sortOrder, sortDirection, districtFilterData);

        setPager(prevPager => {
            const totalPages = Math.ceil(totalCounts / pageSize);
            const visiblePages = 4;
            const pageOffset = Math.max(0, Math.floor((page - 1) / visiblePages) * visiblePages);
            const newPages = [];
            for (let i = pageOffset + 1; i <= Math.min(totalPages, pageOffset + visiblePages); i++) {
                newPages.push(i);
            }
            return {
                ...prevPager,
                currentPage: page,
                totalPages,
                pages: newPages,
            }
        })
    }

    useEffect(() => {
        const currentPager = getPager(totalCounts, 1, pageSize);
        setPager(currentPager)
    }, [totalCounts]);

    useEffect(() => {
        if (selectedState) {
            if (districtFilterData?.statename) {
                setPage();
            }
        }
        else {
            setPage();
        }
    }, [districtFilterData, sortOrder, sortDirection, pageSize])

    //--------------------------|| FILTER ||------------------------------------------//
    const handleFilterOpen = () => {
        setIsEdit();
        props.openDistrictFilterDialog();
    }
    const handleFilterClose = () => {
        props.closeDistrictFilterDialog();
    }
    //--------------------------|| CREATE ||------------------------------------------//
    const handleRegionCreateOpen = () => {
        setIsEdit();
        props.openDistrictCreateDialog();
    }
    const handleRegionCreateClose = () => {
        props.closeDistrictCreateDialog();
    }

    //--------------------------|| EDIT ||------------------------------------------//
    const handleStateSelect = (option) => {
        const { label, value } = option || {};
        setUpdateDistrict({
            ...updateDistrict,
            stateName: label,
            stateId: value
        })
        setErrors({
            ...errors,
            stateName: null,
        })
    }

    const validateOnchange = (e) => {
        const { name, value, type, checked } = e.target;
        setUpdateDistrict({
            ...updateDistrict,
            [name]: type === "checkbox" ? checked : value,
        })
        setErrors({
            ...errors,
            [name]: null,
        })
    }

    const handleUpdateDistrict = (e) => {
        e.preventDefault();
        const { district, stateName } = updateDistrict || {};
        const currentErrors = {};

        if (!district && !district?.trim()) {
            currentErrors.district = 'District name is required';
        }
        if (!stateName && !stateName?.trim()) {
            currentErrors.stateName = 'State/Export name is required';
        }

        setErrors(currentErrors);

        if (Object.keys(currentErrors).length === 0) {
            props.districtEdit(updateDistrict, Pager && Pager.currentPage, pageSize, sortOrder, sortDirection, districtFilterData)
                .then((result) => {
                    if (result) {
                        setIsEdit();
                        setUpdateDistrict();
                        setErrors();
                    }
                })
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleUpdateDistrict(e);
        }
        else {
            AllowedSpecialCharacters(e);
        }
    }

    return (
        <>
            {/*----------------------------|| HEADER ||--------------------------------*/}
            <SubCard sx={{
                p: 1.5,
                pl: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}
            >
                <Grid item sx={{ display: 'flex', gap: { lg: 2, md: 2, sm: 1, xs: 1 }, alignItems: "center", marginRight: "10px" }}>
                    <Typography variant="h4">District List - </Typography>
                    <Tooltip title="District Count" placement="top">
                        <span>
                            <Chip
                                label={totalCounts}
                                chipcolor="secondary"
                                size="small"
                                sx={{
                                    borderRadius: "20%",
                                    py: 1,
                                    fontWeight: "bold",
                                    fontSize: 15,
                                    cursor: "pointer"
                                }}
                            />
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                    <Badge
                        badgeContent={districtFilterData ? Object.keys(districtFilterData).length : 0}
                        color="secondary"
                        invisible={districtFilterData ? Object.keys(districtFilterData).length === 0 : true}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleFilterOpen}
                            startIcon={<IconFilter />}
                        >
                            Filter
                        </Button>
                    </Badge>
                    {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                        <Tooltip title={stateStatus === false ? "District creation is not allowed because the state is inactive." : ""} placement="top">
                            <span style={{ cursor: "pointer" }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleRegionCreateOpen}
                                    startIcon={<IconPlaylistAdd />}
                                    disabled={stateStatus === false}
                                >
                                    Create District
                                </Button>
                            </span>
                        </Tooltip>
                    }
                </Grid>
            </SubCard>
            {/*----------------------------|| TABLE ||--------------------------------*/}
            <MainCard sx={{ marginTop: '15px' }}>
                {errorMessage && <ErrorAlert type={'error'} content={errorMessage} />}
                {districtEditErrorMessage && <ErrorAlert type={'error'} content={`Update District Error : ${districtEditErrorMessage}`} />}
                <TableContainer sx={{ whiteSpace: "nowrap", maxHeight: `calc(${tableHeight}px - 380px)`, overflow: "auto" }}>
                    <Table size="small" stickyHeader aria-label="sticky table"
                        sx={{
                            '& tr > *:last-child': {
                                position: 'sticky',
                                right: 0,
                                bgcolor: 'white',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('District')}
                                        endIcon={sortOrder === 'District' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        District Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('StateName')}
                                        endIcon={sortOrder === 'StateName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        State/Export Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('IsActive')}
                                        endIcon={sortOrder === 'IsActive' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Active ?
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('CreatedBy')}
                                        endIcon={sortOrder === 'CreatedBy' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Created By
                                    </Button>
                                </TableCell>
                                {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                                    <TableCell>
                                        Action
                                    </TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(districtList?.length > 0) && !isPending ?
                                districtList?.map((data, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {isEdit === data.Id ?
                                                <TextField
                                                    autoFocus
                                                    autoComplete="off"
                                                    size="small"
                                                    placeholder="Enter district name"
                                                    name="district"
                                                    onChange={validateOnchange}
                                                    onKeyDown={handleKeyDown}
                                                    onPaste={AllowedSpecialCharactersPaste}
                                                    value={updateDistrict?.district || ""}
                                                    error={errors?.district}
                                                    helperText={errors?.district}
                                                />
                                                : (data.District || 'N/A')
                                            }
                                        </TableCell>
                                        <TableCell sx={{ width: "200px" }}>
                                            {isEdit === data.Id ?
                                                <VWDropdown
                                                    label="State/Export Name"
                                                    name="stateName"
                                                    multiple={false}
                                                    getOptions={loadStateOption}
                                                    onSelect={handleStateSelect}
                                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                                    selectedOption={updateDistrict?.stateName && updateDistrict?.stateId ? { label: updateDistrict?.stateName, value: updateDistrict?.stateId } : null}
                                                    options={stateOptions}
                                                    setOptions={(options) => {
                                                        setStateOptions((prev) => unionBy(prev, options, "value"));
                                                    }}
                                                    isRequired={true}
                                                    error={errors?.stateName}
                                                    isDisabled={selectedState || false}
                                                    onKeyDown={handleKeyDown}
                                                />
                                                : (data.StateName || 'N/A')
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {isEdit === data.Id ?
                                                <Checkbox
                                                    name="isActive"
                                                    onChange={validateOnchange}
                                                    checked={updateDistrict?.isActive}
                                                    onKeyDown={handleKeyDown}
                                                />
                                                : (data.IsActive ?
                                                    <Chip label={"Active"} chipcolor="success" size="small" />
                                                    : <Chip label={"Inactive"} chipcolor="error" size="small" />
                                                )}
                                        </TableCell>
                                        <TableCell>{data.CreatedBy || 'N/A'}<br />
                                            <small>{data.CreatedTimeStamp ? moment(data.CreatedTimeStamp).format("DD MMM YYYY") : "N/A"}</small>
                                        </TableCell>
                                        {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                                            <TableCell>
                                                {isEdit === data.Id ?
                                                    isDistrictEditPending ?
                                                        <Grid container alignItems={"center"} gap={1}>
                                                            <CircularProgress size={20} />
                                                            <Typography variant="h6" color={'primary'}> Loading...</Typography>
                                                        </Grid>
                                                        :
                                                        <Grid container columnGap={2}>
                                                            <Tooltip title="Submit" placement="top">
                                                                <IconCheck
                                                                    style={{ cursor: "pointer", color: "green" }}
                                                                    onClick={handleUpdateDistrict}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip title="Cancel" placement="top">
                                                                <IconX
                                                                    style={{ cursor: "pointer", color: "red" }}
                                                                    onClick={() => {
                                                                        setIsEdit();
                                                                        setUpdateDistrict();
                                                                        setErrors();
                                                                        props.districtEditErrorClear();
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </Grid>
                                                    : stateStatus === false ?
                                                        <Tooltip title={stateStatus === false ? "District edit is not allowed because the state is inactive." : ""} placement="top">
                                                            <span style={{ cursor: "pointer" }}>
                                                                <Button disabled>
                                                                    <IconEdit />
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                        :
                                                        <Tooltip title="Edit District" placement="top">
                                                            <IconEdit
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => {
                                                                    setIsEdit(data.Id);
                                                                    setErrors();
                                                                    setUpdateDistrict({
                                                                        district: data.District,
                                                                        stateName: data.StateName,
                                                                        stateId: data.StateId,
                                                                        isActive: data.IsActive,
                                                                        id: data.Id
                                                                    });
                                                                }}
                                                            />
                                                        </Tooltip>
                                                }
                                            </TableCell>}
                                    </TableRow>
                                ))
                                /*--------|| LOADING ||---------*/
                                : isPending ?
                                    <TableRow >
                                        <TableCell colSpan={8} height='300px'>
                                            <Grid container alignItems={"center"} justifyContent={"center"} gap={1}
                                                sx={{
                                                    position: "sticky",
                                                    width: "fit-content",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)"
                                                }}
                                            >
                                                <CircularProgress />
                                                <Typography variant="h5" color={'primary'}> Loading...</Typography>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                    /*--------|| NO DATA ||---------*/
                                    : <TableCell colSpan={8} height='300px'>
                                        <NoData />
                                    </TableCell>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {/*--------|| PAGINATION ||---------*/}
                <Grid>
                    <VWPagination
                        pageDetails={Pager}
                        pageChange={setPage}
                        totalRecords={totalCounts}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </MainCard >
            <FilterDistrict
                open={isDistrictFilterDialogOpen}
                selectedState={selectedState}
                onClose={handleFilterClose}
                setPage={setPage}
            />
            <CreateDistrict
                filter={districtFilterData}
                selectedState={selectedState}
                selectedStateId={selectedStateId}
                paginationData={{
                    page: (Pager && Pager.currentPage) || 1,
                    pageSize: pageSize,
                    sortDirection: sortDirection,
                    sortOrder: sortOrder
                }}
                open={isDistrictCreateDialogOpen}
                onClose={handleRegionCreateClose}
            />
        </>
    )
}

const mapStateToProps = (state) => ({
    //--------------------List--------------------------------------
    districtList: state.districts.districtList,
    totalCounts: state.districts.districtListCount,
    isPending: state.districts.isDistrictListPending,
    errorMessage: state.districts.districtListErrorMessage,
    //--------------------Filter------------------------------------
    isDistrictFilterDialogOpen: state.districts.isDistrictFilterDialogOpen,
    districtFilterData: state.districts.districtFilterData,
    //--------------------Create------------------------------------
    isDistrictCreateDialogOpen: state.districts.isDistrictCreateDialogOpen,
    //--------------------Edit--------------------------------------
    isDistrictEditPending: state.districts.isDistrictEditPending,
    districtEditErrorMessage: state.districts.districtEditErrorMessage,
    //--------------------Profile-----------------------------------
    userProfile: state.login.userProfile,
})

const mapDispatchToProps = (dispatch) => ({
    //--------------------List--------------------------------------
    getDistrictList: (page, pageSize, sortOrder, sortDirection, filter) => dispatch(getDistrictList(page, pageSize, sortOrder, sortDirection, filter)),
    clearDistrictList: () => dispatch(clearDistrictList()),
    districtListErrorClear: () => dispatch(districtListErrorClear()),
    //--------------------Filter------------------------------------
    openDistrictFilterDialog: () => dispatch(openDistrictFilterDialog()),
    closeDistrictFilterDialog: () => dispatch(closeDistrictFilterDialog()),
    setDistrictFilteredData: (filter) => dispatch(setDistrictFilteredData(filter)),
    //--------------------Create------------------------------------
    openDistrictCreateDialog: () => dispatch(openDistrictCreateDialog()),
    closeDistrictCreateDialog: () => dispatch(closeDistrictCreateDialog()),
    //--------------------Edit--------------------------------------
    districtEdit: (formData, page, pageSize, sortOrder, sortDirection, filter) => dispatch(districtEdit(formData, page, pageSize, sortOrder, sortDirection, filter)),
    districtEditErrorClear: () => dispatch(districtEditErrorClear()),
    getStateOption: (filter, page, pageSize) => dispatch(getStateOptionCreate(filter, page, pageSize)),

})
export default connect(mapStateToProps, mapDispatchToProps)(DistrictList);